import {
  MODEL_CATALOG, TOOL_CALL_PARSERS, REASONING_PARSERS, buildDeploymentPayload,
  customModel, defaultEngine, type CatalogModel, type DeploymentForm,
} from "./catalog";
import type { PredefinedDeployment } from "./types";

export const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

export function resourcesFor(model: CatalogModel) {
  if (model.weightsGb && model.weightsGb < 1)
    return { cpuRequest: "500m", cpuLimit: "2", memoryRequest: "2Gi", memoryLimit: "4Gi", storage: "2Gi" };
  const weights = model.weightsGb || 16;
  const memory = `${Math.ceil(weights * 1.5 + 4)}Gi`;
  return { cpuRequest: "2", cpuLimit: "4", memoryRequest: memory, memoryLimit: memory, storage: `${Math.ceil(weights * 1.3 + 2)}Gi` };
}

export function initialForm(model: CatalogModel): DeploymentForm {
  const suffix = Array.from(crypto.getRandomValues(new Uint8Array(3)), (b) => b.toString(16).padStart(2, "0")).join("");
  return {
    name: `${slug(model.id)}-${suffix}`, model, clusterId: "", namespace: "bunyan-workload",
    gpuType: "nvidia-l4", gpuCount: 1, routing: "round-robin",
    ...resourcesFor(model), engine: defaultEngine(model),
  };
}

export function validatePresetSearch(search: Record<string, unknown>): { preset?: string; presetError?: true } {
  if (!("preset" in search)) return {};
  const value = search.preset;
  const preset = typeof value === "string" ? value.trim() : typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? String(value) : "";
  return preset && preset.length <= 128 ? { preset } : { presetError: true };
}

export function formFromPreset(preset: PredefinedDeployment): DeploymentForm {
  const path = preset.model_url.replace(/^https:\/\/huggingface\.co\//, "").replace(/\/$/, "");
  if (preset.is_active === false || preset.model_source !== "hf" || preset.credentials || preset.args?.is_llm === false || !/^[\w.-]+\/[\w.-]+$/.test(path))
    throw new Error("unsupportedPreset");
  const baseModel = MODEL_CATALOG.find((m) => m.hfPath === path) || customModel(path);
  const model: CatalogModel = { ...baseModel, id: `org-${preset.id ?? preset.name}`, label: preset.name, source: "organization" };
  const form = initialForm(model);
  const resources = preset.resources || {};
  const llm = preset.args?.llm || {};
  const dynamo = preset.args?.dynamo || {};
  if (["is_embedding", "is_voice", "speculative_model", "is_matryoshka", "matryoshka_dimensions"].some((key) => Boolean(llm[key])) ||
      (["is_multimodal", "is_image", "is_video"].some((key) => Boolean(llm[key])) && llm.enable_multimodal !== true) ||
      (llm.tool_call_parser && llm.enable_auto_tool_choice === false) || (resources.gpu_count != null && resources.gpu_count < 1) || resources.autopilot || resources.gpu_type?.toLowerCase().includes("amd"))
    throw new Error("unsupportedPreset");
  if ((dynamo.mode && dynamo.mode !== "aggregated") || (dynamo.replicas != null && dynamo.replicas !== 1) ||
      (preset.max_replicas != null && preset.max_replicas !== 1) || (dynamo.routing && !["kv", "round-robin"].includes(dynamo.routing)))
    throw new Error("unsupportedPreset");
  if ((llm.tool_call_parser && !TOOL_CALL_PARSERS.includes(llm.tool_call_parser as typeof TOOL_CALL_PARSERS[number])) ||
      (llm.reasoning_parser && !REASONING_PARSERS.includes(llm.reasoning_parser as typeof REASONING_PARSERS[number])))
    throw new Error("unsupportedPreset");
  const llmExtras = Object.fromEntries(Object.entries(llm).filter(([key]) => !["max_model_len", "max_num_seqs", "gpu_memory_utilization", "load_format", "tool_call_parser", "reasoning_parser"].includes(key)));
  const dynamoExtras = Object.fromEntries(Object.entries(dynamo).filter(([key]) => !["mode", "replicas", "routing", "sleep_mode", "kv_offload", "max_model_len", "max_num_seqs", "gpu_memory_utilization"].includes(key)));
  return {
    ...form,
    llmExtras,
    dynamoExtras,
    clusterId: preset.cluster_id || preset.model_metadata?.cluster_id || "",
    namespace: preset.namespace || form.namespace,
    gpuType: resources.gpu_type || form.gpuType,
    gpuCount: resources.gpu_count ?? form.gpuCount,
    cpuRequest: resources.cpu_request || form.cpuRequest, cpuLimit: resources.cpu_limit || form.cpuLimit,
    memoryRequest: resources.memory_request || form.memoryRequest, memoryLimit: resources.memory_limit || form.memoryLimit,
    storage: resources.storage || form.storage,
    routing: dynamo.routing === "kv" ? "kv" : "round-robin",
    engine: {
      ...form.engine,
      maxModelLen: llm.max_model_len ?? dynamo.max_model_len ?? form.engine.maxModelLen,
      maxNumSeqs: llm.max_num_seqs ?? dynamo.max_num_seqs ?? form.engine.maxNumSeqs,
      gpuMemoryUtilization: llm.gpu_memory_utilization ?? dynamo.gpu_memory_utilization ?? form.engine.gpuMemoryUtilization,
      toolCallParser: (llm.tool_call_parser || "") as DeploymentForm["engine"]["toolCallParser"],
      reasoningParser: (llm.reasoning_parser || "") as DeploymentForm["engine"]["reasoningParser"],
      hotSwitch: dynamo.sleep_mode?.enabled ?? false,
      fastLoad: llm.load_format === "runai_streamer",
      kvOffloadEnabled: dynamo.kv_offload?.enabled ?? false,
      kvOffloadRamGib: dynamo.kv_offload?.ram_gib ?? form.engine.kvOffloadRamGib,
    },
  };
}

export function recipePayload(form: DeploymentForm, name: string, description: string) {
  const payload = buildDeploymentPayload(form);
  delete payload.cluster_id;
  return { ...payload, name: name.trim(), description: description.trim(), model_metadata: { cluster_id: form.clusterId || null } };
}
